import { AnalyticsEvent, AnalyticsConfig } from "../types/analytics";
import { webActivityService } from "./webActivityService";
import { getConfig } from "../config/environment";

class AnalyticsService {
  private config: AnalyticsConfig;
  private initialized: boolean = false;
  private initializing: boolean = false;
  private queue: AnalyticsEvent[] = [];
  private processingQueue: boolean = false;
  private sessionId: string = "";
  private gaClientId: string = "";
  private gclid: string = "";

  constructor() {
    this.config = {};
    if (typeof window !== "undefined") {
      // Get gclid from URL if present
      const urlParams = new URLSearchParams(window.location.search);
      this.gclid = urlParams.get("gclid") || "";
    }
  }

  async initialize(config: AnalyticsConfig) {
    if (typeof window === "undefined") {
      console.log("Skipping analytics initialization on server-side");
      return;
    }

    // Idempotent across React StrictMode double-invokes and provider remounts:
    // the one-time setup below (web activity, session, queue) runs once. A later
    // call only refreshes the stored config. GTM/GA/Google Ads script injection
    // is owned solely by GTMInjector — this service never injects, resets, or
    // duplicates analytics scripts or the dataLayer.
    if (this.initialized || this.initializing) {
      this.config = config;
      return;
    }
    this.initializing = true;

    try {
      this.config = config;

      // Session id for this service's own events.
      this.sessionId = this.generateSessionId();

      // Read an existing GA client id if a GA tracker is already present (from
      // GTMInjector's real GTM/GA scripts). Best-effort — never throws or logs
      // a failure when GA simply is not configured for this tenant.
      if ((window as any).ga) {
        try {
          (window as any).ga((tracker: any) => {
            this.gaClientId = tracker?.get?.("clientId") || this.gaClientId;
          });
        } catch {
          // GA present but not ready for a client-id read; ignore.
        }
      }

      // Web activity service — this service's responsibility, independent of
      // any GTM/GA configuration.
      const tenantId = (window as any).__TENANT_ID__ || "unknown";
      const orgId = (window as any).__ORG_ID__;
      const apiUrl = getConfig().cloudRunServices.webActivity;
      webActivityService.initialize({ apiUrl, tenantId, orgId });

      this.initialized = true;

      // Process any events queued before initialization completed.
      await this.processQueue();
    } catch (error) {
      console.error("Failed to initialize analytics:", error);
      if (error instanceof Error) {
        console.error("Error name:", error.name);
        console.error("Error message:", error.message);
      }
      throw error;
    } finally {
      this.initializing = false;
    }
  }

  private generateSessionId(): string {
    return (
      "session_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9)
    );
  }

  private async processQueue() {
    if (this.processingQueue || !this.initialized) return;

    this.processingQueue = true;
    while (this.queue.length > 0) {
      const event = this.queue.shift();
      if (event) {
        await this.processEvent(event);
      }
    }
    this.processingQueue = false;
  }

  private async processEvent(event: AnalyticsEvent) {
    try {
      if (!event.data) {
        event.data = {};
      }

      // GA/GTM dispatch removed — conversions are driven by explicit
      // dataLayer.push({event: "Complete", ...}) calls at the call sites
      // (e.g. SqueezePageOverlay registration success). Custom events here
      // only feed the Speculo Web Activity API below.

      // Map event types to backend expectations
      let eventType = "custom";
      if (event.eventType === "pageview") {
        eventType = "page_view";
      } else if (event.eventType === "search") {
        eventType = "search";
      } else if (event.eventType === "interaction" && event.data?.propertyId) {
        if (event.action === "Save Property") {
          eventType = "property_saved";
        } else {
          eventType = "property_view";
        }
      } else if (event.eventType === "conversion") {
        eventType = "lead_capture";
      } else if (event.eventType === "authentication") {
        eventType = "authentication";
      }

      // Send to web activity service
      await webActivityService.trackEvent({
        event_type: eventType,
        event_category: event.category,
        event_action: event.action,
        event_label: event.label,
        event_value: event.value,
        user_id: event.userId,
        properties: {
          ...event.data,
          ga_client_id: this.gaClientId,
          gclid: this.gclid || event.data?.gclid,
          is_authenticated: event.isAuthenticated,
          user_type: event.userType,
          original_event_type: event.eventType,
        },
      });
    } catch (error) {
      console.error("Error processing analytics event:", error);
    }
  }

  // Track page view
  async trackPageView(path?: string) {
    await webActivityService.trackPageView({
      path: path || window.location.pathname,
      title: document.title,
    });
  }

  async trackEvent(
    event: Omit<AnalyticsEvent, "sessionId" | "gaClientId" | "timestamp">
  ) {
    const fullEvent: AnalyticsEvent = {
      ...event,
      sessionId: this.sessionId,
      gaClientId: this.gaClientId,
      timestamp: Date.now(),
    };

    if (!this.initialized) {
      this.queue.push(fullEvent);
      return;
    }

    await this.processEvent(fullEvent);
  }

  async identifyUser(userId: string) {
    // GA/GTM user_id binding removed — Web Activity API tracks user identity below.
    await webActivityService.trackEvent({
      event_type: "authentication",
      event_category: "User",
      event_action: "Identify",
      user_id: userId,
    });
  }
}

export const analyticsService = new AnalyticsService();
